import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Code2,
  Globe,
  Layers,
  LayoutDashboard,
  ShieldCheck,
  Zap,
  Check,
  ArrowRight,
  ExternalLink,
  ChevronDown,
  Terminal,
  Activity,
  Server,
  Lock,
  Workflow,
  Key,
  BarChart3,
  CreditCard,
  Sliders,
  CheckCircle2,
  ArrowUpRight,
  Database,
  Smartphone,
  Eye,
  RefreshCw,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { BrowserMock } from "../components/Visuals";
import { openConsultationModal } from "../components/ConsultationModal";
import { LEGAL_ENTITY, PROJECTS } from "../data";

/* =========================================================================
   DATA DEFINITIONS (Concise, Editorial & Authentic to Neuravolt)
   ========================================================================= */

/* 1. Service Explorer Categories (Light Section) */
const SERVICE_CATEGORIES = [
  {
    id: "websites",
    name: "Websites",
    label: "Websites",
    desc: "High-performance websites designed around your brand and business goals.",
    capabilities: [
      "Business and corporate websites",
      "Custom digital commerce experiences",
      "Technical SEO and sub-second performance foundations",
    ],
    ctaText: "Start Your Website Project",
    modalSubject: "Corporate Website Development",
    previewType: "website",
  },
  {
    id: "web-apps",
    name: "Web Applications",
    label: "Web Applications",
    desc: "Custom applications that simplify workflows and connect business operations.",
    capabilities: [
      "Real-time operational dashboards",
      "Role-based authentication and security",
      "Bi-directional API and ERP integrations",
    ],
    ctaText: "Start Your Web App Project",
    modalSubject: "Custom Web Application Development",
    previewType: "app",
  },
  {
    id: "saas",
    name: "SaaS Platforms",
    label: "SaaS Platforms",
    desc: "Scalable digital products designed for evolving business requirements.",
    capabilities: [
      "Isolated multi-tenant architecture",
      "Automated subscription billing engines",
      "In-app usage telemetry and product analytics",
    ],
    ctaText: "Build Your SaaS Platform",
    modalSubject: "SaaS Platform Engineering",
    previewType: "saas",
  },
  {
    id: "portals",
    name: "Business Portals",
    label: "Business Portals",
    desc: "Connected digital spaces for customers, employees, and partners.",
    capabilities: [
      "Customer self-service consoles",
      "Enterprise administration and audit trails",
      "Granular role-based permission matrices",
    ],
    ctaText: "Develop Your Portal",
    modalSubject: "Business Portal Development",
    previewType: "portal",
  },
];

/* 2. Visual Capabilities Showcase Items (Dark Section) */
const CAPABILITY_ITEMS = [
  {
    id: "dashboards",
    title: "Dashboards and analytics",
    short: "Live metrics, event tracking, and operational business intelligence.",
    icon: BarChart3,
    badge: "ANALYTICS ENGINE",
    detail: "Interactive visualization components with sub-second data streaming and granular date range filtering.",
  },
  {
    id: "auth",
    title: "User authentication",
    short: "Secure sessions, OAuth2, multi-factor verification, and SSO.",
    icon: Lock,
    badge: "ZERO TRUST AUTH",
    detail: "Encrypted JWT and cookie-based sessions with biometric webauthn support and enterprise SAML ready.",
  },
  {
    id: "apis",
    title: "API integrations",
    short: "RESTful endpoints, GraphQL, webhooks, and third-party data sync.",
    icon: Workflow,
    badge: "SYSTEM GATEWAY",
    detail: "High-throughput asynchronous event loops connecting CRMs, payment providers, and legacy databases.",
  },
  {
    id: "automation",
    title: "Workflow automation",
    short: "Background worker queues, triggered alerts, and automated pipelines.",
    icon: Zap,
    badge: "EVENT DISPATCHER",
    detail: "Distributed worker nodes handling email notifications, PDF compilation, and data transformations.",
  },
  {
    id: "payments",
    title: "Payments and subscriptions",
    short: "PCI-compliant checkout, recurring billing, and automated invoicing.",
    icon: CreditCard,
    badge: "COMMERCE LEDGER",
    detail: "Stripe and Razorpay webhook reconciliation with metered billing, GST calculation, and invoice delivery.",
  },
  {
    id: "permissions",
    title: "Administration and permissions",
    short: "Granular roles, organization scoping, and compliance audit logs.",
    icon: Key,
    badge: "RBAC MATRIX",
    detail: "Multi-level authorization matrices restricting access across tenant workspaces and sensitive actions.",
  },
  {
    id: "security",
    title: "Security and performance",
    short: "Sub-second loading, DDoS shielding, OWASP defenses, and 99.98% uptime.",
    icon: ShieldCheck,
    badge: "EDGE DEFENSE",
    detail: "Cloudflare WAF integration, rate limiting, automated SSL rotation, and zero-downtime rolling deploys.",
  },
];

/* 3. 4-Stage Development Process (Dark Section) */
const PROCESS_STAGES = [
  {
    step: "01",
    title: "Discover",
    desc: "Understand business goals, user requirements, and technical constraints.",
  },
  {
    step: "02",
    title: "Design",
    desc: "Plan user experience flows, system architecture, and API contracts.",
  },
  {
    step: "03",
    title: "Build",
    desc: "Develop responsive frontends, scalable backends, and automated test suites.",
  },
  {
    step: "04",
    title: "Launch",
    desc: "Deploy to production infrastructure with monitoring and ongoing support.",
  },
];

/* 4. Frequently Asked Questions (Light Section) */
const FAQS = [
  {
    q: "How much does a custom website or web application cost?",
    a: "Pricing is based on technical scope, required integrations, user roles, and security compliance. We provide transparent fixed-price milestone roadmaps with clearly defined deliverables so you know exactly what is included before development begins.",
  },
  {
    q: "How long does a typical development project take?",
    a: "High-performance corporate websites typically launch in 2 to 4 weeks. Custom web applications and MVP SaaS platforms usually require 6 to 10 weeks depending on backend workflows and third-party integrations. We operate in two-week agile sprints with working staging builds.",
  },
  {
    q: "Can you modernize or rebuild an existing system without downtime?",
    a: "Yes. We specialize in phased migrations. We maintain URL structures and 301 redirects to protect existing search engine authority, and we use zero-downtime deployment pipelines to ensure business continuity.",
  },
  {
    q: "Can you connect a modern web app to our existing database or APIs?",
    a: "Yes. If you have an established database or proprietary backend, we engineer high-speed, modern React frontends that interface cleanly with your existing infrastructure via secure API bridges.",
  },
  {
    q: "What support and maintenance do you provide after launch?",
    a: "Every launch includes post-release stabilization support. We also offer ongoing SLA maintenance agreements covering proactive uptime monitoring, security patches, regular off-site backups, and bi-weekly feature enhancements.",
  },
];

/* =========================================================================
   PAGE COMPONENT
   ========================================================================= */

export default function WebDevelopment() {
  // State for Section 2: Service Explorer
  const [activeCategory, setActiveCategory] = useState("web-apps");

  // State for Section 3: Capabilities Showcase
  const [activeCapability, setActiveCapability] = useState("dashboards");

  // State for Section 6: FAQ Accordion
  const [openFaq, setOpenFaq] = useState(null);

  // State for Hero HUD live toggle
  const [heroHudTab, setHeroHudTab] = useState("metrics");

  // Filter 3 real projects for portfolio showcase
  const portfolioProjects = PROJECTS.filter((p) =>
    ["indusdeal", "paineasy", "meadylife"].includes(p.slug)
  );

  const activeService =
    SERVICE_CATEGORIES.find((s) => s.id === activeCategory) ||
    SERVICE_CATEGORIES[0];

  const activeCap =
    CAPABILITY_ITEMS.find((c) => c.id === activeCapability) ||
    CAPABILITY_ITEMS[0];

  // Schema.org structured data
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Web Development & Digital Products",
      description:
        "High-performance websites, custom web applications, SaaS platforms, and business portals engineered around real operational requirements by Neuravolt.",
      url: "https://neuravolt.in/services/web-development",
      provider: {
        "@type": "Organization",
        name: "Neuravolt Technology",
        legalName: LEGAL_ENTITY,
        url: "https://neuravolt.in",
      },
      areaServed: { "@type": "Country", name: "India" },
      serviceType: "Software Development, Web Applications, SaaS, Portals",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.a,
        },
      })),
    },
  ];

  return (
    <Layout>
      <Seo
        title="Web Application Development Company | High-Performance Web Platforms | NeuraVolt"
        description="High-performance web application development company building conversion-focused web platforms, client portals, and cloud SaaS backends from Gwalior to global markets."
        keywords="web application development company, web development company, custom web application development, web development company in gwalior, website development company in gwalior, enterprise web portals, NeuraVolt"
        path="/services/web-development"
        jsonLd={jsonLd}
      />

      {/* ─────────────────────────────────────────────────────────────────
          SECTION 1: DARK HERO SECTION (Homepage-Inspired Visual Language)
          ───────────────────────────────────────────────────────────────── */}
      <section className="section-dark relative min-h-0 lg:min-h-[92svh] flex flex-col justify-center overflow-hidden grid-bg-dark pt-32 pb-16 sm:pt-40 sm:pb-24 px-5 sm:px-8 border-b border-white/10">
        {/* Restrained Ambient Orange Illumination */}
        <div
          className="blob w-[520px] h-[520px] bg-[#FF5A1F] -top-32 -left-32 pointer-events-none"
          style={{ opacity: 0.25 }}
        />
        <div
          className="blob w-[420px] h-[420px] bg-[#FF5A1F] bottom-10 -right-24 pointer-events-none"
          style={{ opacity: 0.18 }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Column: Core Positioning & CTAs */}
            <div className="lg:col-span-7">
              {/* Eyebrow Label */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full glass-dark text-xs sm:text-sm font-medium tracking-wide text-neutral-300 mb-6"
              >
                <span className="w-2 h-2 rounded-full bg-[#FF5A1F] animate-pulse" />
                <span>WEB DEVELOPMENT &amp; DIGITAL PRODUCTS</span>
              </motion.div>

              {/* Main Headline with Orange Accent */}
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 }}
                className="font-display font-bold tracking-tight text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl"
              >
                Digital Products,{" "}
                <span className="text-gradient">Engineered for Growth</span>.
              </motion.h1>

              {/* Concise Supporting Text */}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.16 }}
                className="mt-6 text-base sm:text-lg text-neutral-400 leading-relaxed max-w-2xl font-normal"
              >
                From high-performance websites to custom applications and SaaS platforms, we build digital products around your business.
              </motion.p>

              {/* Two Functional Actions */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.24 }}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                <button
                  type="button"
                  onClick={() => openConsultationModal("Web Development & Digital Products")}
                  className="px-7 py-3.5 sm:py-4 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm sm:text-base hover:bg-[#ff6d38] transition-all shadow-lg shadow-[#FF5A1F]/25 hover:shadow-xl hover:shadow-[#FF5A1F]/35 flex items-center gap-2 group cursor-pointer active:scale-95"
                >
                  <span>Start Your Project</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <a
                  href="#service-explorer"
                  className="px-7 py-3.5 sm:py-4 rounded-full glass-dark hover:border-[#FF5A1F] text-white font-semibold text-sm sm:text-base transition-all active:scale-95"
                >
                  Explore Services
                </a>
              </motion.div>

              {/* Restrained Capability Ticker */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="mt-10 pt-6 border-t border-white/10 text-xs sm:text-sm text-neutral-400 font-mono tracking-wide"
              >
                Websites <span className="text-[#FF5A1F]">·</span> Web Applications <span className="text-[#FF5A1F]">·</span> SaaS Platforms <span className="text-[#FF5A1F]">·</span> Business Portals
              </motion.div>
            </div>

            {/* Right Column: Realistic Product Interface Composition */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="rounded-2xl border border-white/10 bg-[#111317]/95 backdrop-blur-xl shadow-2xl shadow-black/80 overflow-hidden">
                {/* Browser Window Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/80 inline-block" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80 inline-block" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80 inline-block" />
                    <span className="ml-2 font-mono text-xs text-neutral-400 truncate max-w-[170px]">
                      neuravolt.in/runtime
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-mono text-emerald-400 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    LIVE 99.98%
                  </div>
                </div>

                {/* Sub-Header Tabs */}
                <div className="grid grid-cols-2 border-b border-white/10 bg-black/40 text-[11px] font-mono">
                  <button
                    type="button"
                    onClick={() => setHeroHudTab("metrics")}
                    className={`py-2 px-3 text-center transition-colors cursor-pointer ${
                      heroHudTab === "metrics"
                        ? "text-[#FF5A1F] bg-white/[0.04] font-semibold border-b-2 border-b-[#FF5A1F]"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    Production Metrics
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeroHudTab("topology")}
                    className={`py-2 px-3 text-center transition-colors cursor-pointer ${
                      heroHudTab === "topology"
                        ? "text-[#FF5A1F] bg-white/[0.04] font-semibold border-b-2 border-b-[#FF5A1F]"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    System Architecture
                  </button>
                </div>

                {/* Body Content */}
                <div className="p-5 font-mono text-xs min-h-[260px] flex flex-col justify-between">
                  {heroHudTab === "metrics" ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                          <span className="text-[10px] text-neutral-500 uppercase block mb-1">
                            Response Latency
                          </span>
                          <span className="text-xl font-bold text-white font-display">
                            38ms P95
                          </span>
                          <span className="text-[10px] text-emerald-400 block mt-1">
                            Async Event Loops
                          </span>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                          <span className="text-[10px] text-neutral-500 uppercase block mb-1">
                            Page Load (FCP)
                          </span>
                          <span className="text-xl font-bold text-white font-display">
                            &lt; 0.4s
                          </span>
                          <span className="text-[10px] text-emerald-400 block mt-1">
                            100/100 Lighthouse
                          </span>
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 space-y-2">
                        <div className="flex items-center justify-between text-neutral-300">
                          <span className="text-[11px] text-neutral-400">Database Engine</span>
                          <span className="text-white font-semibold">PostgreSQL ACID + Redis</span>
                        </div>
                        <div className="flex items-center justify-between text-neutral-300">
                          <span className="text-[11px] text-neutral-400">Security Standard</span>
                          <span className="text-emerald-400">Zero-Trust RBAC</span>
                        </div>
                        <div className="flex items-center justify-between text-neutral-300">
                          <span className="text-[11px] text-neutral-400">Deployment</span>
                          <span className="text-[#FF5A1F]">Docker Blue/Green Cutover</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] text-neutral-500 block">01. Edge</span>
                          <span className="text-white text-xs">Cloudflare WAF / CDN</span>
                        </div>
                        <span className="text-emerald-400 text-[10px] font-bold">&lt; 12ms</span>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] text-neutral-500 block">02. Client</span>
                          <span className="text-white text-xs">React 19 / Next.js SSR</span>
                        </div>
                        <span className="text-emerald-400 text-[10px] font-bold">&lt; 45ms</span>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] text-neutral-500 block">03. Storage</span>
                          <span className="text-white text-xs">Multi-Tenant Isolated Schema</span>
                        </div>
                        <span className="text-emerald-400 text-[10px] font-bold">SECURE</span>
                      </div>
                    </div>
                  )}

                  {/* Footer Bar */}
                  <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-neutral-400">
                    <span className="flex items-center gap-1.5">
                      <Server className="w-3.5 h-3.5 text-[#FF5A1F]" />
                      <span>Host: neuravolt.cloud</span>
                    </span>
                    <span className="text-neutral-400">TLS 1.3 Active</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────
          SECTION 2: LIGHT INTERACTIVE SERVICE EXPLORER
          ───────────────────────────────────────────────────────────────── */}
      <section id="service-explorer" className="py-20 sm:py-28 px-5 sm:px-8 mesh-light border-b border-black/5">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="max-w-3xl mb-12">
            <span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] mb-3">
              <span className="w-6 h-px bg-[#FF5A1F]" />
              OUR CAPABILITIES
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#0B0B0B] leading-[1.15] mb-4">
              What Do You Want to Build?
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 leading-relaxed font-normal">
              Explore digital solutions designed around your business.
            </p>
          </div>

          {/* Compact Category Selector Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-10 p-1.5 rounded-2xl bg-black/[0.04] border border-black/5 w-fit">
            {SERVICE_CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                    isActive
                      ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/25"
                      : "text-neutral-700 hover:text-black hover:bg-black/5"
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Shared Content Area with Responsive Split */}
          <div className="rounded-3xl p-6 sm:p-10 bg-white border border-black/10 shadow-lg relative overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              {/* Left Column: Details & Capabilities */}
              <div className="lg:col-span-5 flex flex-col justify-between">
                <div>
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F] block mb-2">
                    SERVICE ARCHETYPE
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B] mb-3">
                    {activeService.name}
                  </h3>
                  <p className="text-neutral-600 text-sm sm:text-base leading-relaxed mb-6 font-normal">
                    {activeService.desc}
                  </p>

                  <div className="space-y-3 mb-8">
                    {activeService.capabilities.map((cap, cIdx) => (
                      <div key={cIdx} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 text-[#FF5A1F] flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-3 h-3" />
                        </span>
                        <span className="text-xs sm:text-sm text-neutral-800 font-medium leading-relaxed">
                          {cap}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => openConsultationModal(activeService.modalSubject)}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF5A1F] text-white text-xs sm:text-sm font-semibold hover:bg-[#ff6d38] transition-all shadow-md shadow-[#FF5A1F]/20 cursor-pointer active:scale-95"
                  >
                    <span>{activeService.ctaText}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Right Column: Realistic Interface Preview */}
              <div className="lg:col-span-7">
                <div className="rounded-2xl border border-black/10 bg-[#0B0B0B] text-white p-5 sm:p-6 shadow-xl overflow-hidden font-sans">
                  {/* Preview Title Bar */}
                  <div className="flex items-center justify-between pb-3 mb-4 border-b border-white/10 text-xs text-neutral-400 font-mono">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-neutral-600 inline-block" />
                      <span className="text-neutral-300">{activeService.name} Preview</span>
                    </div>
                    <span className="text-[10px] text-[#FF5A1F] uppercase font-bold">
                      Interactive Model
                    </span>
                  </div>

                  {/* Dynamic Simulation Body */}
                  {activeService.previewType === "website" && (
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center font-display font-bold">
                            NV
                          </div>
                          <span className="font-semibold text-sm text-white">Brand Platform</span>
                        </div>
                        <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-mono font-bold">
                          100/100 Core Web Vitals
                        </span>
                      </div>
                      <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 space-y-2">
                        <div className="h-4 w-3/4 bg-white/20 rounded" />
                        <div className="h-3 w-1/2 bg-white/10 rounded" />
                        <div className="pt-2 flex items-center gap-2">
                          <span className="px-3 py-1 rounded-full bg-[#FF5A1F] text-white text-[10px] font-semibold">
                            Primary CTA
                          </span>
                          <span className="px-3 py-1 rounded-full bg-white/10 text-neutral-300 text-[10px]">
                            Secondary Link
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono text-neutral-400 pt-1">
                        <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5">
                          <span className="text-white font-bold block text-xs">0.38s</span> FCP Speed
                        </div>
                        <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5">
                          <span className="text-white font-bold block text-xs">0 CLS</span> Layout Shift
                        </div>
                        <div className="p-2 rounded-lg bg-white/[0.02] border border-white/5">
                          <span className="text-white font-bold block text-xs">Schema.org</span> SEO Valid
                        </div>
                      </div>
                    </div>
                  )}

                  {activeService.previewType === "app" && (
                    <div className="space-y-3 font-mono text-xs">
                      <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/10">
                        <span className="text-white font-semibold">Workflow Console</span>
                        <span className="text-[10px] text-emerald-400 font-bold">● ACTIVE SESSION</span>
                      </div>
                      <div className="space-y-2 text-[11px]">
                        <div className="flex items-center justify-between p-2 rounded bg-black/40 border border-white/5">
                          <span className="text-neutral-400">Order Dispatch Queue</span>
                          <span className="text-emerald-400 font-bold">Synced (12ms)</span>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded bg-black/40 border border-white/5">
                          <span className="text-neutral-400">Role-Based Access</span>
                          <span className="text-white">Admin / Operator / Auditor</span>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded bg-black/40 border border-white/5">
                          <span className="text-neutral-400">REST API Gateway</span>
                          <span className="text-[#FF5A1F]">200 OK P95 38ms</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeService.previewType === "saas" && (
                    <div className="space-y-3 font-mono text-xs">
                      <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] text-neutral-500 uppercase block">Workspace</span>
                          <span className="text-white font-bold text-sm">Enterprise Multi-Tenant</span>
                        </div>
                        <span className="px-2 py-0.5 rounded bg-[#FF5A1F]/20 text-[#FF5A1F] text-[10px] font-bold">
                          PRO PLAN
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px]">
                        <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                          <span className="text-neutral-400 block mb-0.5">Automated Billing</span>
                          <span className="text-white font-bold text-xs">Stripe Webhooks OK</span>
                        </div>
                        <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                          <span className="text-neutral-400 block mb-0.5">Schema Isolation</span>
                          <span className="text-emerald-400 font-bold text-xs">Row-Level Security</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeService.previewType === "portal" && (
                    <div className="space-y-3 font-mono text-xs">
                      <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-between">
                        <span className="text-white font-semibold">Client Vault &amp; Ledger</span>
                        <span className="text-[10px] text-blue-400 font-bold">ENCRYPTED</span>
                      </div>
                      <div className="space-y-1.5 text-[10px]">
                        <div className="p-2 rounded bg-black/40 border border-white/5 flex items-center justify-between">
                          <span className="text-neutral-300">Self-Service Profile</span>
                          <span className="text-emerald-400">Verified</span>
                        </div>
                        <div className="p-2 rounded bg-black/40 border border-white/5 flex items-center justify-between">
                          <span className="text-neutral-300">Signed Documents &amp; Invoices</span>
                          <span className="text-[#FF5A1F]">PDF Download</span>
                        </div>
                        <div className="p-2 rounded bg-black/40 border border-white/5 flex items-center justify-between">
                          <span className="text-neutral-300">Audit Trail Logging</span>
                          <span className="text-neutral-400">Immutable</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────
          SECTION 3: DARK CAPABILITIES SHOWCASE
          ───────────────────────────────────────────────────────────────── */}
      <section className="section-dark py-20 sm:py-28 px-5 sm:px-8 grid-bg-dark border-b border-white/10 relative overflow-hidden">
        {/* Subtle orange accent glow */}
        <div
          className="blob w-[450px] h-[450px] bg-[#FF5A1F] top-1/4 right-0 pointer-events-none"
          style={{ opacity: 0.15 }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] mb-3">
              <span className="w-6 h-px bg-[#FF5A1F]" />
              ENGINEERED FOR REAL-WORLD NEEDS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-white leading-[1.15] mb-4">
              More Than Just an Interface.
            </h2>
            <p className="text-base sm:text-lg text-neutral-400 leading-relaxed font-normal">
              Beyond visual design, we engineer resilient backend systems, secure authentication, and high-velocity workflows.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            {/* Left Column: Compact Selectable Capabilities List */}
            <div className="lg:col-span-5 space-y-2">
              {CAPABILITY_ITEMS.map((item) => {
                const Icon = item.icon;
                const isSelected = activeCapability === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveCapability(item.id)}
                    className={`w-full p-4 rounded-2xl text-left transition-all flex items-center justify-between border cursor-pointer ${
                      isSelected
                        ? "bg-white/[0.06] border-[#FF5A1F] shadow-lg shadow-[#FF5A1F]/10"
                        : "bg-white/[0.01] border-white/5 hover:border-white/15 hover:bg-white/[0.03]"
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                          isSelected
                            ? "bg-[#FF5A1F] text-white"
                            : "bg-white/5 text-[#FF5A1F] border border-white/10"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-white text-sm">
                          {item.title}
                        </h4>
                        <p className="text-neutral-400 text-xs font-normal leading-relaxed line-clamp-1">
                          {item.short}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-[#FF5A1F] shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right Column: Central Realistic Application Preview */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-white/10 bg-[#121418] p-6 sm:p-8 shadow-2xl relative overflow-hidden">
                {/* Subsystem Header */}
                <div className="flex items-center justify-between pb-5 border-b border-white/10 mb-6">
                  <div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF5A1F] block mb-1">
                      {activeCap.badge}
                    </span>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-white">
                      {activeCap.title}
                    </h3>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono text-[10px] font-bold">
                    VERIFIED SUBSYSTEM
                  </span>
                </div>

                <p className="text-neutral-300 text-sm leading-relaxed mb-6 font-normal">
                  {activeCap.detail}
                </p>

                {/* Subsystem Simulated Architecture Console */}
                <div className="p-4 sm:p-5 rounded-2xl bg-black/60 border border-white/10 font-mono text-xs space-y-3">
                  <div className="flex items-center justify-between text-[11px] text-neutral-400 pb-2 border-b border-white/5">
                    <span>Subsystem: {activeCap.id}</span>
                    <span className="text-emerald-400">Status: HEALTHY</span>
                  </div>

                  {activeCapability === "dashboards" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Event Throughput:</span>
                        <span className="text-white font-bold">14,200 events/sec</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Query Aggregation:</span>
                        <span className="text-emerald-400">sub-20ms TimescaleDB</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Real-Time Push:</span>
                        <span className="text-[#FF5A1F]">WebSocket Multiplex</span>
                      </div>
                    </div>
                  )}

                  {activeCapability === "auth" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Auth Protocol:</span>
                        <span className="text-white font-bold">OAuth 2.0 / OpenID Connect</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Token Security:</span>
                        <span className="text-emerald-400">HttpOnly SameSite Strict JWT</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Multi-Factor Auth:</span>
                        <span className="text-[#FF5A1F]">TOTP / Hardware WebAuthn</span>
                      </div>
                    </div>
                  )}

                  {activeCapability === "apis" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Endpoint Specification:</span>
                        <span className="text-white font-bold">OpenAPI 3.1 Contract</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Rate Limiting:</span>
                        <span className="text-emerald-400">Token Bucket Redis Engine</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Webhook Guarantees:</span>
                        <span className="text-[#FF5A1F]">Idempotent Signature Check</span>
                      </div>
                    </div>
                  )}

                  {activeCapability === "automation" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Queue Engine:</span>
                        <span className="text-white font-bold">BullMQ / Celery Redis Pool</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Failure Retry Policy:</span>
                        <span className="text-emerald-400">Exponential Backoff</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Job Concurrency:</span>
                        <span className="text-[#FF5A1F]">Multi-Worker Parallelism</span>
                      </div>
                    </div>
                  )}

                  {activeCapability === "payments" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Compliance Level:</span>
                        <span className="text-white font-bold">PCI-DSS Level 1 Safe</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Gateways Supported:</span>
                        <span className="text-emerald-400">Stripe &amp; Razorpay Webhooks</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Tax &amp; Invoicing:</span>
                        <span className="text-[#FF5A1F]">Automated GST Ledger Receipts</span>
                      </div>
                    </div>
                  )}

                  {activeCapability === "permissions" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Access Matrix:</span>
                        <span className="text-white font-bold">Role-Based Access Control</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Tenant Scoping:</span>
                        <span className="text-emerald-400">PostgreSQL Row-Level Policies</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Security Audit:</span>
                        <span className="text-[#FF5A1F]">Immutable Action Audit Trail</span>
                      </div>
                    </div>
                  )}

                  {activeCapability === "security" && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-neutral-300">
                        <span>Network Protection:</span>
                        <span className="text-white font-bold">Cloudflare Enterprise WAF</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Encryption:</span>
                        <span className="text-emerald-400">TLS 1.3 &amp; Encrypted at Rest</span>
                      </div>
                      <div className="flex justify-between text-neutral-300">
                        <span>Uptime Guarantee:</span>
                        <span className="text-[#FF5A1F]">99.98% High Availability</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs text-neutral-400 font-mono">
                    Architecture tailored to your stack.
                  </span>
                  <button
                    type="button"
                    onClick={() => openConsultationModal("Technical Architecture Consultation")}
                    className="inline-flex items-center gap-1.5 text-xs text-[#FF5A1F] font-semibold hover:text-[#ff6d38] transition-colors cursor-pointer"
                  >
                    <span>Discuss Requirements</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────
          SECTION 4: LIGHT PORTFOLIO SECTION (Real Work & Genuine Projects)
          ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 mesh-light border-b border-black/5" id="portfolio">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] mb-3">
              <span className="w-6 h-px bg-[#FF5A1F]" />
              PROVEN IMPACT
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#0B0B0B] leading-[1.15] mb-4">
              Built for Real-World Challenges.
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 leading-relaxed font-normal">
              Real platforms engineered and deployed by Neuravolt for demanding operational requirements.
            </p>
          </div>

          {/* Genuine Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioProjects.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <div className="gborder glass-card rounded-3xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-2 flex flex-col h-full justify-between">
                  <div>
                    {/* Browser Mockup Image Frame */}
                    <Link to={`/portfolio/${p.slug}`} className="block relative overflow-hidden rounded-2xl group/img mb-5">
                      <BrowserMock
                        src={p.img}
                        alt={p.title}
                        domain={p.domain || "neuravolt.in"}
                        className="aspect-[16/10] group-hover/img:scale-[1.02] transition-transform duration-500"
                      />
                      <span className="absolute top-3 right-3 text-xs font-semibold px-3 py-1 rounded-full bg-[#FF5A1F] text-white shadow-md">
                        {p.cat}
                      </span>
                      {p.domain && (
                        <span className="absolute bottom-3 left-3 text-[11px] font-mono font-medium px-2.5 py-1 rounded-lg bg-black/80 backdrop-blur-md text-white border border-white/20">
                          {p.domain}
                        </span>
                      )}
                    </Link>

                    {/* Title & Tagline */}
                    <div className="px-1">
                      <Link to={`/portfolio/${p.slug}`} className="group/title block mb-2">
                        <h3 className="font-heading text-xl font-bold text-[#0B0B0B] group-hover/title:text-[#FF5A1F] transition-colors">
                          {p.title}
                        </h3>
                      </Link>

                      <div className="flex items-center gap-1.5 text-xs text-[#FF5A1F] font-medium mb-3 bg-[#FF5A1F]/5 px-2.5 py-1 rounded-lg w-fit border border-[#FF5A1F]/15">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        <span>{p.badge || "Engineered by Neuravolt"}</span>
                      </div>

                      {/* Concise 1-sentence description */}
                      <p className="text-neutral-600 text-sm leading-relaxed mb-4 line-clamp-2 font-normal">
                        {p.desc}
                      </p>
                    </div>
                  </div>

                  {/* Card Bottom Meta */}
                  <div className="px-1 pt-4 border-t border-black/5 flex items-center justify-between mt-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {(p.tech || []).slice(0, 2).map((t) => (
                        <span key={t} className="text-xs px-2.5 py-0.5 rounded-full bg-black/5 text-neutral-600 font-medium">
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-2.5">
                      {p.domain && (
                        <a
                          href={p.live_url || `https://${p.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-neutral-700 font-medium hover:text-[#FF5A1F] transition-colors bg-black/5 hover:bg-black/10 px-2.5 py-1 rounded-full cursor-pointer"
                        >
                          <span>Live</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <Link
                        to={`/portfolio/${p.slug}`}
                        className="inline-flex items-center gap-1 text-[#FF5A1F] font-semibold text-xs sm:text-sm hover:underline cursor-pointer"
                      >
                        Case Study <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Central Portfolio Link */}
          <div className="mt-12 text-center">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#0B0B0B] text-white text-sm font-semibold hover:bg-[#FF5A1F] transition-colors shadow-lg hover:shadow-[#FF5A1F]/20 group"
            >
              <span>Explore All Verified Work</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────
          SECTION 5: DARK DEVELOPMENT PROCESS (Compact 4 Stages)
          ───────────────────────────────────────────────────────────────── */}
      <section className="section-dark py-20 sm:py-28 px-5 sm:px-8 grid-bg-dark border-b border-white/10" id="process">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] mb-3">
              <span className="w-6 h-px bg-[#FF5A1F]" />
              OUR METHODOLOGY
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-white leading-[1.15] mb-4">
              A Disciplined Engineering Process.
            </h2>
            <p className="text-base sm:text-lg text-neutral-400 leading-relaxed font-normal">
              A structured, milestone-driven progression from discovery to production launch.
            </p>
          </div>

          {/* 4 Connected Stages: Desktop Horizontal Line, Mobile Stacked Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STAGES.map((st, idx) => (
              <Reveal key={st.step} delay={idx * 0.08}>
                <div className="p-6 sm:p-7 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#FF5A1F]/40 transition-all flex flex-col justify-between h-full group">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="w-10 h-10 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 text-[#FF5A1F] font-mono font-bold text-sm flex items-center justify-center group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors">
                        {st.step}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                        PHASE {idx + 1}
                      </span>
                    </div>

                    <h3 className="font-display text-xl font-bold text-white mb-2">
                      {st.title}
                    </h3>
                    <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed font-normal">
                      {st.desc}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/5 flex items-center gap-2 text-[11px] font-mono text-[#FF5A1F]">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Milestone Verified</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────
          SECTION 6: LIGHT FAQ & FINAL CONVERSATION CTA
          ───────────────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 mesh-light" id="faq">
        <div className="max-w-4xl mx-auto">
          {/* FAQ Header */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 text-[#FF5A1F] font-heading text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] mb-3">
              <span className="w-6 h-px bg-[#FF5A1F]" />
              FREQUENTLY ASKED QUESTIONS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B0B0B] mb-3">
              Questions &amp; Answers
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base font-normal">
              Transparent guidance on pricing, milestones, migrations, and support.
            </p>
          </div>

          {/* Compact Accordion */}
          <div className="space-y-3.5 mb-20">
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-black/10 bg-white overflow-hidden shadow-xs transition-all"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4 cursor-pointer hover:bg-black/[0.01] transition-colors"
                  >
                    <span className="font-heading font-semibold text-[#0B0B0B] text-base">
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-[#FF5A1F] shrink-0 transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 text-neutral-600 text-sm leading-relaxed border-t border-black/5 pt-3 font-normal">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Final Callout Card: Have a Product in Mind? */}
          <div className="rounded-3xl p-8 sm:p-14 bg-[#0B0B0B] text-white border border-black/10 relative overflow-hidden shadow-2xl text-center">
            {/* Subtle Ambient Blob */}
            <div
              className="blob w-[400px] h-[400px] bg-[#FF5A1F] -bottom-32 -right-20 pointer-events-none"
              style={{ opacity: 0.22 }}
            />

            <div className="relative z-10 max-w-2xl mx-auto">
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#FF5A1F] block mb-3">
                HAVE A PRODUCT IN MIND?
              </span>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white mb-4">
                Have a Product in Mind?
              </h2>
              <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mb-8 font-normal">
                Tell us what you want to build. Let's define the next step.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => openConsultationModal("Web Development & Digital Products")}
                  className="px-8 py-4 rounded-full bg-[#FF5A1F] text-white font-semibold text-base hover:bg-[#ff6d38] transition-all shadow-lg shadow-[#FF5A1F]/30 hover:shadow-xl hover:shadow-[#FF5A1F]/40 flex items-center gap-2 cursor-pointer active:scale-95"
                >
                  <span>Discuss Your Project</span>
                  <ArrowRight className="w-4 h-4" />
                </button>

                <Link
                  to="/portfolio"
                  className="px-8 py-4 rounded-full glass-dark hover:border-[#FF5A1F] text-white font-semibold text-base transition-all active:scale-95"
                >
                  Explore Our Work
                </Link>
              </div>

              <p className="text-neutral-500 text-xs sm:text-sm font-mono">
                Direct conversation with an engineer. Fixed-milestone roadmap.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
